import { useMemo } from 'react';

import {
  Paper,
  Table,
  Select,
  MenuItem,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  FormControl,
  ListSubheader,
  TableContainer,
} from '@mui/material';

import { useTranslate } from 'src/locales';

import { ImportFieldGroup } from './import-fields';
import { ImportTarget, ImportMapping } from './import-mapping';

/** Dropdown section order: who the record is first, rarely used ID columns last. */
const GROUP_ORDER: Array<ImportFieldGroup | 'custom'> = [
  'identity',
  'details',
  'company',
  'address',
  'availability',
  'organisation',
  'links',
  'custom',
  'advanced',
];

type Props = {
  headers: string[];
  /** A few values from each column, in column order, to help recognise it. */
  samples: string[][];
  targets: ImportTarget[];
  mapping: ImportMapping;
  onChange: (column: number, targetId: string) => void;
};

export default function ImportColumnMapper({ headers, samples, targets, mapping, onChange }: Props) {
  const { t } = useTranslate();

  const groups = useMemo(
    () =>
      GROUP_ORDER.map((group) => ({ group, targets: targets.filter((target) => target.group === group) })).filter(
        (entry) => entry.targets.length > 0
      ),
    [targets]
  );

  const targetsById = useMemo(
    () => new Map(targets.map((target): [string, ImportTarget] => [target.id, target])),
    [targets]
  );

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('csv_import.column_in_file')}</TableCell>
            <TableCell>{t('csv_import.sample_values')}</TableCell>
            <TableCell sx={{ minWidth: 280 }}>{t('csv_import.import_as')}</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {headers.map((header, column) => {
            const selected = mapping[column] ?? '';
            const hint = selected ? targetsById.get(selected)?.hint : undefined;
            const takenByOtherColumns = new Set(
              Object.keys(mapping)
                .filter((other) => Number(other) !== column)
                .map((other) => mapping[Number(other)])
                .filter(Boolean)
            );

            return (
              <TableRow key={column} hover>
                <TableCell>
                  <Typography variant="subtitle2">{header || `#${column + 1}`}</Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 280 }}>
                    {samples[column]?.join(', ') || '-'}
                  </Typography>
                </TableCell>

                <TableCell>
                  <FormControl fullWidth size="small">
                    <Select
                      value={selected}
                      displayEmpty
                      onChange={(event) => onChange(column, event.target.value as string)}
                      inputProps={{ 'aria-label': `${t('csv_import.import_as')}: ${header}` }}
                      MenuProps={{ PaperProps: { sx: { maxHeight: 420 } } }}
                    >
                      <MenuItem value="">
                        <Typography variant="body2" color="text.secondary">
                          {t('csv_import.dont_import')}
                        </Typography>
                      </MenuItem>

                      {groups.map(({ group, targets: groupTargets }) => [
                        <ListSubheader key={`group-${group}`}>{t(`csv_import.groups.${group}`)}</ListSubheader>,
                        ...groupTargets.map((target) => {
                          const taken = takenByOtherColumns.has(target.id);

                          return (
                            <MenuItem key={target.id} value={target.id} disabled={taken}>
                              {target.label}
                              {taken && (
                                <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 1 }}>
                                  {t('csv_import.used_by_other_column')}
                                </Typography>
                              )}
                            </MenuItem>
                          );
                        }),
                      ])}
                    </Select>
                  </FormControl>

                  {hint && (
                    <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
                      {hint}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
